import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'common/styling/theme';
import { stripRtlWordMarks } from 'common/utils';

import { WatchIcon } from 'common/icons';

import BaseTextField from '../BaseTextField';
import FieldHelpText from '../../FieldHelpText';
import ActionsButton from '../../ActionsButton';

import { getDynamicStyles, staticStyles } from './style';

const ReduxTextField = ({
  id,
  input,
  meta: { dirty, touched, error },
  type,
  tooltipId,
  tooltipTextId,
  tooltipFormattedValues,
  customDefaultValue,
  action,
  actionTooltipId,
  actionClassName,
  description,
  theme,
  onAutofill,
  rtlWords,
  ...props
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [stateValue, setStateValue] = useState(typeof input.value === 'object' ? '' : input.value);

  const handleChange = e => {
    if (rtlWords && typeof e !== 'string') {
      // ar 字段靠 dir="rtl" 天然从右向左输入，输入什么就存什么，不做任何单词序反转；
      // 这里仅清理历史数据里可能残留的方向标记。
      const cleanValue = stripRtlWordMarks(e.target.value);
      input.onChange(cleanValue);
      setStateValue(cleanValue);
      return;
    }
    if (typeof e !== 'string') {
      const { target } = e;

      if (type === 'number' && e.target.value) {
        // eslint-disable-next-line
        if (!isNaN(target.value)) {
          input.onChange(target.value);
          setStateValue(target.value);
        }
        if (input.name === 'amount') {
          const number = Number(target.value.replace(/[,]/g, ''));
          input.onChange(number);
          setStateValue(number);
        }
      } else {
        input.onChange(target.value);
        setStateValue(target.value);
      }
    } else {
      input.onChange(e);
      setStateValue(e);
    }
  };

  const handleBlur = e => {
    if (rtlWords && typeof e !== 'string') {
      // 不做翻转，失焦时只把当前值（清理标记后）写回 store，所见即所存。
      const cleanValue = stripRtlWordMarks(e.target.value);
      input.onBlur(cleanValue);
      setStateValue(cleanValue);
      return;
    }
    if (typeof e !== 'string') {
      const { target } = e;

      if (type === 'number' && target.value) {
        // eslint-disable-next-line
        if (!isNaN(target.value)) {
          if (target.value.slice(-1) === '.') {
            input.onBlur(target.value);
            setStateValue(target.value);
          } else {
            input.onBlur(Number(target.value));
            setStateValue(Number(target.value));
          }
        }
      } else {
        input.onBlur(target.value);
        setStateValue(target.value);
      }
    } else {
      input.onBlur(e);
      setStateValue(e);
    }
  };
  const value = input.value ? stateValue : input.value;
  const fieldValue = value || (typeof input.value === 'object' ? '' : input.value);

  return (
    <>
      <BaseTextField
        id={id}
        {...props}
        {...input}
        type={type === 'number' ? 'text' : type}
        additionalClass
        value={rtlWords ? stripRtlWordMarks(fieldValue) : fieldValue}
        dir={rtlWords ? 'rtl' : undefined}
        name={input.name}
        error={(dirty || touched) && error}
        customDefaultValue={customDefaultValue}
        onChange={handleChange}
        onFocus={input.onFocus}
        onBlur={handleBlur}
        onAutofill={onAutofill}>
        <span className="ReduxTextField__error">{(dirty || touched) && error}</span>

        <FieldHelpText tooltipId={tooltipId} tooltipTextId={tooltipTextId} formattedValues={tooltipFormattedValues} />

        {!!action && (
          <ActionsButton
            condition={!!action}
            tooltipId={input.name}
            tooltipTextId={actionTooltipId}
            watch
            className={actionClassName}
            onClickFunc={action}>
            <WatchIcon />
          </ActionsButton>
        )}

        <style jsx global>
          {staticStyles}
        </style>
        <style jsx global>
          {dynamicStyles}
        </style>
      </BaseTextField>
      {!!description && !tooltipTextId && <span className="ReduxTextField__helpText">{description}</span>}
    </>
  );
};

ReduxTextField.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  input: PropTypes.object.isRequired,
  meta: PropTypes.shape({
    dirty: PropTypes.bool.isRequired,
    touched: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }).isRequired,
  type: PropTypes.string,
  tooltipId: PropTypes.string,
  tooltipTextId: PropTypes.string,
  tooltipFormattedValues: PropTypes.object,
  customDefaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  action: PropTypes.func,
  actionTooltipId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  actionClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.string,
  rtlWords: PropTypes.bool,
  theme: PropTypes.object,
};

ReduxTextField.defaultProps = {
  id: null,
  type: 'text',
  tooltipId: '',
  tooltipTextId: '',
  tooltipFormattedValues: {},
  customDefaultValue: '',
  action: null,
  actionTooltipId: null,
  actionClassName: null,
  description: '',
  rtlWords: false,
  theme: {},
};

export default withTheme()(ReduxTextField);
export { ReduxTextField };
